<template>
  <!-- Utilización de la Membresía Section -->
  <div class="membership-usage-section">
    <h2><b>Utilización del Seguro Inteligente Mutuus</b></h2>
    <div class="section-layout">
      <div v-if="!loadingMembershipUsage" class="membership-usage-content">
        <!-- Left Card: Summary -->
        <div class="membership-usage-summary-card">
          <div class="membership-usage-header">
            <div class="section-header">
              <div class="icon-wrapper">
                <img src="@/onboarding/assets/company-icon.svg" alt="Services" />
              </div>
              <h3 class="subsection-title">Resumen de utilización</h3>
            </div>
            <p class="usage-description">
              Tus colaboradores han aprovechado los diversos beneficios de su cobertura de la
              siguiente forma:
            </p>
          </div>
          <div class="usage-boxes-list">
            <!-- Consulta de nutrición -->
            <div class="usage-box-item">
              <div class="usage-box blue">
                <img src="@/onboarding/assets/apple-icon.svg" alt="Nutrition" />
                <span class="usage-box-number">{{ nutritionCount }}</span>
              </div>
              <span class="usage-box-text">Consulta de nutrición</span>
            </div>
            <!-- Psicoterapia -->
            <div class="usage-box-item">
              <div class="usage-box blue">
                <img src="@/onboarding/assets/brain-icon.svg" alt="Psychology" />
                <span class="usage-box-number">{{ psychologyCount }}</span>
              </div>
              <span class="usage-box-text">Psicoterapia</span>
            </div>
            <!-- Especialista -->
            <div class="usage-box-item">
              <div class="usage-box blue">
                <img src="@/onboarding/assets/stethoscope-icon.svg" alt="Specialist" />
                <span class="usage-box-number">{{ specialistTotal }}</span>
              </div>
              <span class="usage-box-text">Especialista</span>
            </div>
            <!-- Otros servicios -->
            <div class="usage-box-item">
              <div class="usage-box blue">
                <img src="@/onboarding/assets/another-services-icon.svg" alt="Other Services" />
                <span class="usage-box-number">{{ otherServicesTotal }}</span>
              </div>
              <span class="usage-box-text">Otros servicios</span>
            </div>
          </div>
        </div>

        <!-- Right Card: Details -->
        <div class="membership-usage-details-card">
          <!-- Check up Anual -->
          <div class="usage-service-item">
            <div class="service-header">
              <div class="service-icon-wrapper">
                <img src="@/onboarding/assets/microscope-icon.svg" alt="Check Up" />
              </div>
              <div class="service-info">
                <div class="service-header-row">
                  <span class="service-name">Check up Anual</span>
                  <div class="service-count-badge">
                    {{ checkUpService.count }} / {{ maxAnnualUsage }}
                  </div>
                </div>
                <div class="service-progress-bar-container">
                  <div
                    class="service-progress-bar"
                    :style="{
                      width: getProgressPercentage(checkUpService.count, maxAnnualUsage) + '%',
                    }"
                  ></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Limpieza dental Anual -->
          <div class="usage-service-item">
            <div class="service-header">
              <div class="service-icon-wrapper">
                <img src="@/onboarding/assets/tooth-icon.svg" alt="Dental Cleaning" />
              </div>
              <div class="service-info">
                <div class="service-header-row">
                  <span class="service-name">Limpieza dental Anual</span>
                  <div class="service-count-badge">
                    {{ dentalCleaningService.count }} / {{ maxAnnualUsage }}
                  </div>
                </div>
                <div class="service-progress-bar-container">
                  <div
                    class="service-progress-bar"
                    :style="{
                      width:
                        getProgressPercentage(dentalCleaningService.count, maxAnnualUsage) + '%',
                    }"
                  ></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Total Services Used -->
          <div class="total-services-box">
            <img src="@/onboarding/assets/mutuus-services.svg" alt="Total" />
            <div class="total-services-content">
              <span class="total-services-number">{{ totalServicesUsed }}</span>
              <span class="total-services-description"
                >Total de servicios usados por tus colaboradores</span
              >
            </div>
            <div class="total-services-info-button">
              <InfoButton
                :messages="[
                  'Son todos los servicios que tus colaboradores han utilizado desde la contratación del Seguro Médico Inteligente Mutuus',
                ]"
                type="info"
              />
            </div>
          </div>
        </div>
      </div>
      <div v-else class="loading-membership-usage">Cargando utilización...</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import {
  MembershipUsageResponseDTO,
  MembershipUsageRequestDTO,
  ServiceNameEnglish,
  SpecialtyNameEnglish,
} from 'commons'
import InfoButton from '@/components/InfoButton.vue'
import { ApiService } from '@/services/apiService'

interface Props {
  groupId: string
}
const props = defineProps<Props>()

const apiService = new ApiService()
const membershipUsageData = ref<MembershipUsageResponseDTO | null>(null)
const loadingMembershipUsage = ref(true)
const maxAnnualUsage = ref(0)

onMounted(async () => {
  await Promise.all([fetchMembershipUsage(), fetchMaxAnnualUsage()])
})

const fetchMembershipUsage = async () => {
  try {
    loadingMembershipUsage.value = true
    const now = new Date()
    const startDate = `${now.getFullYear()}/01/01`
    const endDate = `${now.getFullYear()}/12/31`
    const request = new MembershipUsageRequestDTO(
      startDate,
      endDate,
      [Number(props.groupId)],
      [SpecialtyNameEnglish.PSYCHOLOGY],
      [
        ServiceNameEnglish.NUTRITION,
        ServiceNameEnglish.CHECK_UP,
        ServiceNameEnglish.DENTAL_CLEANING,
      ],
    )
    membershipUsageData.value = await apiService.getMembershipUsage(request)
  } catch (error) {
    console.error('Hubo un error: Error fetching membership usage ', error)
  } finally {
    loadingMembershipUsage.value = false
  }
}

const fetchMaxAnnualUsage = async () => {
  try {
    maxAnnualUsage.value = await apiService.getTotalActiveMemberships(Number(props.groupId))
  } catch (error) {
    console.error('Hubo un error: Error fetching total active memberships ', error)
    maxAnnualUsage.value = 0
  }
}

const nutritionCount = computed(() => {
  if (!membershipUsageData.value) return 0
  const nutritionService = membershipUsageData.value.requestedServices.find(
    (s) => s.nameEN === ServiceNameEnglish.NUTRITION,
  )
  return nutritionService?.count || 0
})

const psychologyCount = computed(() => {
  if (!membershipUsageData.value) return 0
  const psychologySpecialty = membershipUsageData.value.requestedSpecialties.find(
    (s) => s.nameEN === SpecialtyNameEnglish.PSYCHOLOGY,
  )
  return psychologySpecialty?.count || 0
})

const specialistTotal = computed(() => {
  return membershipUsageData.value?.nonRequestedSpecialties?.total || 0
})

const otherServicesTotal = computed(() => {
  return membershipUsageData.value?.nonRequestedServices?.total || 0
})

const checkUpService = computed(() => {
  if (!membershipUsageData.value) return { count: 0 }
  const service = membershipUsageData.value.requestedServices.find(
    (s) => s.nameEN === ServiceNameEnglish.CHECK_UP,
  )
  return service || { count: 0 }
})

const dentalCleaningService = computed(() => {
  if (!membershipUsageData.value) return { count: 0 }
  const service = membershipUsageData.value.requestedServices.find(
    (s) => s.nameEN === ServiceNameEnglish.DENTAL_CLEANING,
  )
  return service || { count: 0 }
})

const totalServicesUsed = computed(() => {
  if (!membershipUsageData.value) return 0
  const specialtiesTotal = membershipUsageData.value.nonRequestedSpecialties?.total || 0
  const servicesTotal = membershipUsageData.value.nonRequestedServices?.total || 0
  return (
    specialtiesTotal +
    servicesTotal +
    psychologyCount.value +
    nutritionCount.value +
    checkUpService.value.count +
    dentalCleaningService.value.count
  )
})

const getProgressPercentage = (current: number, max: number) => {
  if (max === 0) return 100
  const percentage = (current / max) * 100
  return Math.min(percentage, 100)
}
</script>

<style scoped>
/* Section Layout - positions title to the left of content */
.section-layout {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 40px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: visible;
}

/* Utilización de la Membresía Section (styles subset for this component) */
.membership-usage-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 24px;
  width: 100%;
  max-width: 100%;
  flex: none;
  order: 3;
  align-self: stretch;
  flex-grow: 0;
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: visible;
}

.membership-usage-content {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  padding: 32px 40px 32px 16px;
  gap: 40px;
  width: 100%;
  max-width: 100%;
  min-height: 319px;
  height: auto;
  background: #ffffff;
  border: 1px solid #d5d5d5;
  border-radius: 24px;
  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 0;
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: visible;
}

.loading-membership-usage {
  padding: 40px;
  text-align: center;
  font-family: 'Montserrat';
  font-size: 16px;
  color: #2b2a2a;
}

.membership-usage-summary-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;
  gap: 36px;
  margin: 0 auto;
  width: 661px;
  max-width: 100%;
  min-height: 246px;
  height: auto;
  background: #ffffff;
  border-radius: 16px;
  flex: 1 1 auto;
  order: 0;
  flex-grow: 0;
  min-width: 0;
  box-sizing: border-box;
}
.membership-usage-header {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 0px;
  gap: 8px;
  width: 100%;
  max-width: 100%;
  min-height: 92px;
  height: auto;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
  box-sizing: border-box;
}
.membership-usage-header .section-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  gap: 16px;
  width: 100%;
  max-width: 100%;
  min-height: 40px;
  height: auto;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
  box-sizing: border-box;
}
.membership-usage-header .icon-wrapper {
  width: 40px;
  height: 40px;
  background: #bdff9d;
  border-radius: 4px;
  flex: none;
  order: 0;
  flex-grow: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}
.membership-usage-header .icon-wrapper img {
  width: 24px;
  height: 24px;
  object-fit: contain;
}
.membership-usage-header .subsection-title {
  width: 100%;
  max-width: 100%;
  min-height: 25px;
  height: auto;
  font-family: 'Montserrat';
  font-style: normal;
  font-weight: 700;
  font-size: 18px;
  line-height: 140%;
  color: #2b2a2a;
  flex: 1 1 auto;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
  text-align: left;
  box-sizing: border-box;
  word-wrap: break-word;
}
.usage-description {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px 0px 0px 56px;
  gap: 15px;
  width: 100%;
  max-width: 100%;
  min-height: 44px;
  height: auto;
  font-family: 'Montserrat';
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  color: #000000;
  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 0;
  box-sizing: border-box;
  word-wrap: break-word;
}
.usage-boxes-list {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  align-content: flex-start;
  padding: 16px 0px 0px;
  gap: 16px 40px;
  width: 100%;
  max-width: 100%;
  min-height: 118px;
  height: auto;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
  box-sizing: border-box;
}
.usage-box-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;
  gap: 4px;
  width: 123px;
  height: 102px;
  flex: none;
  flex-grow: 0;
}
.usage-box {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 11px 16px;
  gap: 4px;
  width: 123px;
  height: 54px;
  background: var(--primary-blue);
  border-radius: 8px;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
}
.usage-box img {
  width: 32px;
  height: 32px;
  object-fit: contain;
}
.usage-box-number {
  width: 40px;
  height: 31px;
  font-family: 'Montserrat';
  font-style: normal;
  font-weight: 700;
  font-size: 24px;
  line-height: 130%;
  display: flex;
  align-items: center;
  text-align: center;
  color: #fafafa;
}
.usage-box-text {
  width: 123px;
  height: 44px;
  font-family: 'Montserrat';
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  text-align: center;
  color: #2b2a2a;
}

.membership-usage-details-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 32px;
  margin: 0 auto;
  width: 582px;
  max-width: 100%;
  min-height: 255px;
  height: auto;
  flex: 1 1 auto;
  order: 1;
  flex-grow: 0;
  min-width: 0;
  box-sizing: border-box;
}
.usage-service-item {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0px;
  gap: 16px;
  width: 100%;
  max-width: 100%;
  min-height: 48px;
  height: auto;
  box-sizing: border-box;
}
.service-icon-wrapper {
  width: 48px;
  height: 48px;
  min-width: 48px;
  background: var(--primary-blue);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  flex-shrink: 0;
}
.service-icon-wrapper img {
  width: 28.8px;
  height: 28.8px;
  object-fit: contain;
}
.service-header {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0px;
  gap: 16px;
  width: 100%;
  max-width: 100%;
  min-height: 48px;
  height: auto;
  box-sizing: border-box;
}
.service-info {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 0px;
  gap: 8px;
  width: 100%;
  max-width: 100%;
  min-height: 46px;
  height: auto;
  flex-grow: 1;
  min-width: 0;
  box-sizing: border-box;
}
.service-header-row {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0px;
  gap: 8px;
  width: 100%;
  max-width: 100%;
  min-height: 30px;
  height: auto;
  box-sizing: border-box;
}
.service-name {
  width: auto;
  min-width: 0;
  max-width: 100%;
  min-height: 22px;
  height: auto;
  font-family: 'Montserrat';
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  display: flex;
  align-items: center;
  color: #2b2a2a;
  flex: 0 1 auto;
  box-sizing: border-box;
  word-wrap: break-word;
}
.service-count-badge {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 4px 8px;
  gap: 8px;
  min-width: 70px;
  width: auto;
  height: 30px;
  background: #eaeaea;
  border-radius: 8px;
  flex-shrink: 0;
  font-family: 'Montserrat';
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  text-align: center;
  white-space: nowrap;
  color: #2b2a2a;
  margin-left: auto;
}
.service-progress-bar-container {
  width: 100%;
  max-width: 100%;
  height: 8px;
  background: #becbe0;
  border-radius: 32px;
  position: relative;
  box-sizing: border-box;
  overflow: hidden;
}
.service-progress-bar {
  position: absolute;
  left: 0%;
  top: 12.5%;
  bottom: -12.5%;
  height: 100%;
  background: var(--primary-blue);
  border-radius: 0px 24px 24px 0px;
  transition: width 0.3s ease;
}

.total-services-box {
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 16px;
  gap: 16px;
  isolation: isolate;
  width: 100%;
  max-width: 100%;
  min-height: 95px;
  height: auto;
  background: #9cd41c;
  border: 1px solid #d5d5d5;
  border-radius: 16px;
  position: relative;
}
.total-services-box > img {
  width: 75px;
  height: 75px;
  object-fit: contain;
}
.total-services-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 0px;
  gap: 4px;
  width: 100%;
  max-width: 100%;
  min-height: 59px;
  height: auto;
  z-index: 2;
  flex: 1 1 auto;
  min-width: 0;
  box-sizing: border-box;
}
.total-services-number {
  width: 64px;
  height: 38px;
  font-family: 'Montserrat';
  font-style: normal;
  font-weight: 700;
  font-size: 32px;
  line-height: 120%;
  display: flex;
  align-items: center;
  text-align: center;
  letter-spacing: 1px;
  color: #2b2a2a;
}
.total-services-description {
  width: 100%;
  max-width: 100%;
  min-height: 17px;
  height: auto;
  font-family: 'Montserrat';
  font-style: normal;
  font-weight: 700;
  font-size: 13px;
  line-height: 130%;
  color: #2b2a2a;
  box-sizing: border-box;
  word-wrap: break-word;
}
.total-services-info-button {
  position: absolute;
  width: 32px;
  height: 32px;
  right: 8px;
  top: 8px;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.total-services-info-button :deep(.info-icon) {
  width: 24px;
  height: 24px;
  background-color: #6b6b6b;
}

/* Responsive adjustments for smaller screens */
@media (max-width: 1400px) {
  .membership-usage-content {
    padding: 28px 32px 28px 16px;
    gap: 32px;
  }

  .section-layout {
    gap: 32px;
  }
}

@media (max-width: 1320px) {
  .membership-usage-content {
    padding: 24px 20px 24px 16px;
    gap: 20px;
  }

  .membership-usage-summary-card {
    width: 100%;
    max-width: 100%;
    flex: 1 1 auto;
  }

  .membership-usage-details-card {
    width: 100%;
    max-width: 100%;
    flex: 1 1 auto;
  }

  .usage-description {
    padding: 0px 0px 0px 40px;
    font-size: 14px;
  }

  .usage-boxes-list {
    gap: 16px 24px;
  }

  .service-header-row {
    gap: 6px;
  }

  .service-name {
    font-size: 14px;
  }

  .service-count-badge {
    font-size: 14px;
    padding: 4px 6px;
    min-width: 60px;
  }

  .total-services-box {
    padding: 12px;
    gap: 12px;
  }

  .total-services-number {
    font-size: 28px;
  }

  .total-services-description {
    font-size: 12px;
  }
}

@media (max-width: 1200px) {
  .membership-usage-content {
    flex-direction: column;
    align-items: stretch;
    padding: 24px;
    gap: 24px;
  }

  .membership-usage-summary-card,
  .membership-usage-details-card {
    width: 100%;
    max-width: 100%;
  }

  .usage-description {
    padding: 0px;
  }
}

@media (max-width: 768px) {
  .membership-usage-content {
    padding: 20px;
    gap: 20px;
  }

  .usage-boxes-list {
    gap: 12px 16px;
  }

  .service-header {
    gap: 12px;
  }

  .service-icon-wrapper {
    width: 40px;
    height: 40px;
    min-width: 40px;
  }
}
</style>
